import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import {
    Col,
    IconButton,
    Image,
    Row,
    Text,
} from 'reaxl';

import getPathSRP from '@/utilities/getPathSRP';

import remoteActions from '@/actions/srp/remoteActions';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import authDuck from '@/ducks/authDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import srpContentDuck from '@/ducks/srp/srpContentDuck.js';
import userDuck from '@/ducks/userDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import KeywordSearchContainer from '@/containers/KeywordSearchContainer';
import { KeywordSearchTrigger } from '@/containers/KeywordSearchModalContainer';

import MobileSearchFilterButtons from '@/components/SearchLink/MobileSearchFilterButtons';

import InventoryClearFilters from './InventoryClearFilters';
import SaveSearchContainer from './SaveSearchContainer';
import SrpHeroContainer from './SrpHeroContainer';
import SuggestedFilterContainer from './SuggestedFilterContainer';

function KeywordButton({ onClick, label = 'Search by keyword' }) {
    return (
        <IconButton
            label={label}
            glyph="ai-search"
            hideLabel
            size="medium"
            onClick={onClick}
        />
    );
}

export function SearchResultsTitleContainer() {
    const dispatch = useDispatch();

    const device = useDevice();
    const lessThanMd = _get(device, 'lessThan.md', false);
    const isXs = _get(device, 'is.xs', false);

    const { locationHeading } = useSelector(srpContentDuck.selectors.getDuckState);

    const {
        bookmarkId = null,
        city = '',
        dma = {},
        location = {},
        searchBookmarkId = null,
        sellerType = '',
        state = '',
        zip = '',
    } = useSelector(queryDuck.selectors.getDuckState) || {};

    const savedSearch = useSelector(userDuck.selectors.getSavedSearch) || {};
    const savedSearchTitle = savedSearch.title;
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);

    const navigateToSrp = useSrpNavigation();

    const [backToResultsPath, setPath] = useState('');

    const {
        // feature configs
        advanced_keyword_search: [enableAdvancedKeywordSearch],
        srp_category_search: [enableCategorySearch, { suggested_filters: enableSuggestedFilters }],
        srp_hero: [enableSrpHero, {
            alt_text: altText,
            background_color: backgroundColor,
            cmp: heroCmpId,
            hero_height: heroHeight,
            src: heroSrc,
            logo: logoSrc,
        }],
        suggested_keyword: [enableKeyword],
        srp_redesigned_sticky_header: [redesignedStickyHeader],
    } = useFeatures([
        'advanced_keyword_search',
        'compare',
        'enable_private_seller_exchange_logo',
        'srp_category_search',
        'srp_hero',
        'personalization',
        'psx_logo',
        'suggested_keyword',
        'srp_redesigned_sticky_header',
    ]);

    useEffect(() => {
        if (searchBookmarkId && isLoggedIn) {
            dispatch(remoteActions.loadSavedSearchBookmark());
        }
        if (bookmarkId) {
            dispatch(remoteActions.handleSearchRedirect(false, navigateToSrp));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchBookmarkId]);

    // sets path for cta in SRP hero
    useEffect(() => {
        const getBackToResultsPath = async () => {
            const path = await getPathSRP({ city, dma, experience: null, location, state, zip }, { basePath: true });
            setPath(path);
        };

        if (enableSrpHero) {
            getBackToResultsPath();
        }
    }, [city, dma, enableSrpHero, location, state, zip]);

    const renderFilterChipsControls = (
        <div className="display-flex flex-nowrap gap-3">
            <SaveSearchContainer
                key="saveSearchContainer"
            />
            <InventoryClearFilters
                key="clearFilters"
            />
        </div>
    );

    const handleShowKeywordModal = () => dispatch(activeInteractionDuck.creators.setKeys({
        showKeywordSearchModal: true,
    }));

    const heroContent = (
        <SrpHeroContainer
            backToResultsPath={backToResultsPath}
            locationHeading={locationHeading}
            savedSearchTitle={savedSearchTitle}
            sellerType={sellerType}
        />
    );

    const backgroundPosition = useMemo(() => {
        if (heroHeight) {
            return 'center';
        }
        if (lessThanMd) {
            return 'right 10px top 10px';
        }
        return 'center right';
    }, [heroHeight, lessThanMd]);

    return (
        <>
            <div className={clsx(backgroundColor, { 'container-lg': heroHeight })}>
                <div
                    data-cmp={heroCmpId + '-srp-hero'}
                    className={clsx({ 'container-lg': !heroHeight })}
                    style={{
                        position: 'relative',
                        backgroundImage: `url(${heroSrc})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition,
                        backgroundSize: (heroHeight && 'cover') || (lessThanMd ? '100px' : 'auto 90%'),
                        height: heroHeight,
                    }}

                >
                    {!heroHeight && <div className="padding-vertical-2">{heroContent}</div>}
                    {heroHeight && (
                        <div className="hero-gradient">
                            <div className="display-flex flex-wrap justify-content-center">
                                <div className="display-flex justify-content-center align-items-top gap-2 margin-top-3 text-center">
                                    <div>
                                        <Image
                                            src={logoSrc}
                                            alt={altText}
                                            lazyload={false}
                                            loading="eager"
                                            width={140}
                                        />
                                    </div>
                                    <Text
                                        color="inverse"
                                        size={400}
                                        weight="bold"
                                        componentClass="div"
                                    >
                                        Buy Online Store
                                    </Text>
                                </div>
                                <Text
                                    className="w-100 text-center"
                                    color="inverse"
                                    size={100}
                                >
                                    Sponsored by
                                    {' '}
                                    {altText}
                                </Text>
                            </div>

                            <div className="hero-divider">
                                <svg
                                    preserveAspectRatio="none"
                                    viewBox="0 0 1200 120"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ fill: '#ffffff', width: `${isXs ? '150%' : '125%'}`, height: `${isXs ? '52px' : '75px'}`, transform: 'rotate(180deg) scaleX(-1)' }}
                                >
                                    <path d="M321.39 56.44c58-10.79 114.16-30.13 172-41.86 82.39-16.72 168.19-17.73 250.45-.39C823.78 31 906.67 72 985.66 92.83c70.05 18.48 146.53 26.09 214.34 3V0H0v27.35a600.21 600.21 0 00321.39 29.09z" />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {heroHeight && (
                <div className="container-lg margin-bottom-3">
                    {heroContent}
                </div>
            )}

            {(!enableCategorySearch || enableCategorySearch && enableSuggestedFilters) && isXs && (
                <div
                    className="container-lg padding-sm-0 margin-vertical-sm-3 margin-bottom-3"
                    style={redesignedStickyHeader ? { borderBottom: '1px solid var(--base-color-neutral-300)' } : null}
                >
                    <Row className="display-flex align-items-center justify-content-between padding-vertical-1 padding-vertical-sm-0">
                        {(!isXs && enableAdvancedKeywordSearch) && (
                            <Col
                                sm={5}
                                md={3}
                            >
                                <KeywordSearchTrigger />
                            </Col>
                        )}
                        {(!isXs && !enableAdvancedKeywordSearch) && (
                            <Col
                                sm={5}
                                md={3}
                            >
                                <KeywordSearchContainer />
                            </Col>
                        )}

                        <Col
                            xs={12}
                            sm={enableKeyword ? 7 : 12}
                            md={enableKeyword ? 9 : 12}
                            className="padding-horizontal-0 padding-horizontal-sm-2"
                        >
                            <div
                                className="display-flex justify-items-center align-items-center flex-direction-row w-100"
                            >
                                {enableAdvancedKeywordSearch && (enableKeyword && isXs && <KeywordButton onClick={handleShowKeywordModal} />)}
                                {redesignedStickyHeader && <MobileSearchFilterButtons showSearchButton={false} />}
                                {!lessThanMd && renderFilterChipsControls}
                                <div
                                    className="padding-top-sm-0"
                                    style={{ minWidth: '100px' }}
                                >
                                    <SuggestedFilterContainer />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            )}
        </>
    );
}

export default SearchResultsTitleContainer;
